import { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Sparkle, CheckCircle, Image as ImageIcon } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { suggestVehicles } from "@/lib/vehicle";

const EMPTY = {
  agency_id: "",
  client_name: "",
  client_phone: "",
  pickup_date: "",
  pickup_time: "",
  pickup_location: "",
  dropoff_location: "",
  passenger_count: 1,
  luggage_count: 0,
  vehicle_type: "eco",
  baby_seat: false,
  booster_seat: false,
  flight_number: "",
  train_number: "",
  notes: "",
  admin_price: "",
};

export default function AdminCreateRequestModal({ open, onClose, onCreated }) {
  const [agencies, setAgencies] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [showNewAgency, setShowNewAgency] = useState(false);
  const [newAgency, setNewAgency] = useState({ name: "", email: "", password: "", phone: "", contact_name: "" });
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiApplied, setAiApplied] = useState(false);

  const fetchAgencies = useCallback(async () => {
    try {
      const { data } = await api.get("/agencies");
      setAgencies(data.filter((a) => a.account_status !== "pending"));
    } catch {}
  }, []);

  useEffect(() => {
    if (open) {
      fetchAgencies();
      setForm(EMPTY);
      setShowNewAgency(false);
      setNewAgency({ name: "", email: "", password: "", phone: "", contact_name: "" });
      setAiText("");
      setAiApplied(false);
      setAiBusy(false);
    }
  }, [open, fetchAgencies]);

  const applyAiResult = (data) => {
    if (!data || typeof data !== "object") return;
    const normStr = (v) => (typeof v === "string" ? v : "");
    const normInt = (v, d = 0) => (Number.isFinite(+v) ? parseInt(v, 10) : d);
    const vt = data.vehicle_type === "van" ? "van" : "eco";
    setForm((p) => ({
      ...p,
      client_name: normStr(data.client_name) || p.client_name,
      client_phone: normStr(data.client_phone) || p.client_phone,
      pickup_date: normStr(data.pickup_date) || p.pickup_date,
      pickup_time: normStr(data.pickup_time) || p.pickup_time,
      pickup_location: normStr(data.pickup_location) || p.pickup_location,
      dropoff_location: normStr(data.dropoff_location) || p.dropoff_location,
      passenger_count: normInt(data.passenger_count, p.passenger_count) || 1,
      luggage_count: normInt(data.luggage_count, p.luggage_count) || 0,
      vehicle_type: vt,
      baby_seat: !!data.baby_seat || p.baby_seat,
      booster_seat: !!data.booster_seat || p.booster_seat,
      flight_number: normStr(data.flight_number) || p.flight_number,
      train_number: normStr(data.train_number) || p.train_number,
      notes: normStr(data.notes) || p.notes,
    }));
    setAiApplied(true);
  };

  const runAi = async () => {
    if (!aiText.trim()) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/ride-requests/ai-parse", { text: aiText });
      applyAiResult(data);
      toast.success("Veuillez vérifier les informations avant validation.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "AI failed");
    } finally {
      setAiBusy(false);
    }
  };

  const aiFileRef = useRef(null);
  const runAiImage = async (e) => {
    const file = e.target.files?.[0];
    if (aiFileRef.current) aiFileRef.current.value = "";
    if (!file) return;
    setAiBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ride-requests/ai-parse-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 40000,
      });
      console.log("[AI image] source:", data?._source, "status:", data?._extraction_status);
      console.log("[AI image] OCR text:", data?._ocr_text || "(empty)");
      console.log("[AI image] parsed fields:", data);
      applyAiResult(data);
      const st = data?._extraction_status;
      if (st === "full") toast.success("Champs pré-remplis — vérifiez avant validation");
      else if (st === "partial") toast.info("Lecture partielle — veuillez compléter les informations manquantes");
      else toast.info("Aucune donnée détectée — veuillez remplir manuellement");
    } catch (err) {
      console.warn("[AI image] request failed:", err?.response?.data || err?.message);
      toast.info("Lecture partielle — veuillez compléter les informations manquantes");
    } finally {
      setAiBusy(false);
    }
  };

  const upd = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const toggle = (field) => () => setForm((p) => ({ ...p, [field]: !p[field] }));

  const createQuickAgency = async () => {
    if (!newAgency.name || !newAgency.email || !newAgency.password) {
      toast.error("Agency name, email, password required");
      return;
    }
    try {
      const { data } = await api.post("/agencies", newAgency);
      toast.success("Agency created");
      await fetchAgencies();
      setForm((p) => ({ ...p, agency_id: data.id }));
      setShowNewAgency(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.agency_id) { toast.error("Select or create an agency/client"); return; }
    const required = ["client_name", "client_phone", "pickup_date", "pickup_time", "pickup_location", "dropoff_location"];
    for (const r of required) if (!form[r]) { toast.error(`Missing: ${r.replace("_", " ")}`); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        passenger_count: parseInt(form.passenger_count, 10) || 1,
        luggage_count: parseInt(form.luggage_count, 10) || 0,
        admin_price: form.admin_price === "" ? null : parseFloat(form.admin_price),
        accepted_terms: true,
      };
      await api.post("/ride-requests/admin", payload);
      toast.success("Request created");
      setForm(EMPTY);
      onClose();
      setTimeout(() => { onCreated?.(); }, 100);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="admin-create-request-title">New Booking Request</DialogTitle>
          <DialogDescription className="text-zinc-500">Create a request on behalf of an agency or private client (WhatsApp, phone, email intake).</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-create-request-form">
          {/* ── AI paste block ───────────── */}
          <div data-testid="acr-ai-block" className="rounded-md border border-amber-500/25 bg-amber-500/5 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-amber-400" />
              <p className="text-xs uppercase tracking-wider text-amber-300 font-medium">Remplissage automatique par IA</p>
            </div>
            <Textarea
              data-testid="acr-ai-text"
              rows={3}
              value={aiText}
              onChange={(e) => setAiText(e.target.value)}
              placeholder="Copiez ici votre email, votre message ou votre demande WhatsApp, et les informations seront remplies automatiquement."
              className="bg-[#1A1A1A] border-zinc-700 text-white text-sm"
            />
            <Button
              data-testid="acr-ai-run"
              type="button"
              onClick={runAi}
              disabled={aiBusy || !aiText.trim()}
              className="bg-amber-500 text-black font-semibold hover:bg-amber-600 h-9 w-full"
            >
              {aiBusy ? "Analyse en cours..." : "Analyser"}
            </Button>
            <input
              ref={aiFileRef}
              type="file"
              accept="image/*"
              onChange={runAiImage}
              className="hidden"
              data-testid="acr-ai-image-input"
            />
            <Button
              data-testid="acr-ai-image-btn"
              type="button"
              variant="outline"
              onClick={() => aiFileRef.current?.click()}
              disabled={aiBusy}
              className="h-9 w-full border-sky-500/40 text-sky-400 hover:bg-sky-500/10"
            >
              <ImageIcon size={14} weight="fill" className="mr-1.5" />
              {aiBusy ? "Lecture..." : "Importer un screenshot"}
            </Button>
            {aiApplied && (
              <div data-testid="acr-ai-verify-notice" className="flex items-start gap-2 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-md p-2">
                <CheckCircle size={14} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />
                <span>Veuillez vérifier les informations avant validation. Les champs restent modifiables.</span>
              </div>
            )}
          </div>

          {/* Agency selector */}
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400 uppercase tracking-wider">Agency / Private Client *</Label>
            <div className="flex gap-2 items-stretch">
              <Select value={form.agency_id} onValueChange={(v) => setForm((p) => ({ ...p, agency_id: v }))}>
                <SelectTrigger data-testid="acr-agency-select" className="bg-[#1A1A1A] border-zinc-700 text-white h-10 flex-1">
                  <SelectValue placeholder="Select an account" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-zinc-700 max-h-64">
                  {agencies.map((a) => (
                    <SelectItem key={a.id} value={a.id} className="text-white hover:bg-zinc-800 focus:bg-zinc-800">
                      {a.name} <span className="text-zinc-500">· {a.email}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button data-testid="acr-new-agency-btn" type="button" variant="outline" onClick={() => setShowNewAgency((v) => !v)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                <Plus size={14} className="mr-1" /> New
              </Button>
            </div>
          </div>

          {showNewAgency && (
            <div data-testid="acr-new-agency-block" className="rounded-md border border-zinc-800 bg-[#0e0e0e] p-3 space-y-2">
              <p className="text-xs text-amber-400 uppercase tracking-wider">Create account (pre-approved)</p>
              <Input data-testid="acr-new-agency-name" placeholder="Name (agency or client)" value={newAgency.name} onChange={(e) => setNewAgency((p) => ({ ...p, name: e.target.value }))} className="bg-[#1A1A1A] border-zinc-700 text-white h-9" />
              <Input placeholder="Contact person (optional)" value={newAgency.contact_name} onChange={(e) => setNewAgency((p) => ({ ...p, contact_name: e.target.value }))} className="bg-[#1A1A1A] border-zinc-700 text-white h-9" />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="acr-new-agency-email" type="email" placeholder="Email" value={newAgency.email} onChange={(e) => setNewAgency((p) => ({ ...p, email: e.target.value }))} className="bg-[#1A1A1A] border-zinc-700 text-white h-9" />
                <Input placeholder="Phone" value={newAgency.phone} onChange={(e) => setNewAgency((p) => ({ ...p, phone: e.target.value }))} className="bg-[#1A1A1A] border-zinc-700 text-white h-9" />
              </div>
              <Input data-testid="acr-new-agency-password" type="password" placeholder="Password" value={newAgency.password} onChange={(e) => setNewAgency((p) => ({ ...p, password: e.target.value }))} className="bg-[#1A1A1A] border-zinc-700 text-white h-9" />
              <Button data-testid="acr-new-agency-submit" type="button" onClick={createQuickAgency} className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 h-9">Create & select</Button>
            </div>
          )}

          {/* Client */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Client name *</Label>
              <Input data-testid="acr-client-name" value={form.client_name} onChange={upd("client_name")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Phone *</Label>
              <Input data-testid="acr-client-phone" value={form.client_phone} onChange={upd("client_phone")} placeholder="+33 ..." className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Pickup date *</Label>
              <Input data-testid="acr-pickup-date" type="date" value={form.pickup_date} onChange={upd("pickup_date")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Pickup time *</Label>
              <Input data-testid="acr-pickup-time" type="time" value={form.pickup_time} onChange={upd("pickup_time")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Pickup location *</Label>
              <Input data-testid="acr-pickup-loc" value={form.pickup_location} onChange={upd("pickup_location")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Dropoff location *</Label>
              <Input data-testid="acr-dropoff-loc" value={form.dropoff_location} onChange={upd("dropoff_location")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Passengers</Label>
              <Input data-testid="acr-passengers" type="number" min={1} value={form.passenger_count} onChange={upd("passenger_count")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Luggage</Label>
              <Input data-testid="acr-luggage" type="number" min={0} value={form.luggage_count} onChange={upd("luggage_count")} className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            {(() => {
              const p = parseInt(form.passenger_count || 0);
              if (!p || p < 5) return null;
              const s = suggestVehicles(form.passenger_count, form.luggage_count);
              return (
                <p data-testid="acr-vehicle-suggestion" className="col-span-2 text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded p-2">
                  Suggestion : {s.label}{s.heavy ? " — bagages importants pris en compte." : ""}
                </p>
              );
            })()}
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Vehicle</Label>
              <Select value={form.vehicle_type} onValueChange={(v) => setForm((p) => ({ ...p, vehicle_type: v }))}>
                <SelectTrigger data-testid="acr-vehicle" className="bg-[#1A1A1A] border-zinc-700 text-white h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-zinc-700">
                  <SelectItem value="eco" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Eco (up to 4)</SelectItem>
                  <SelectItem value="van" className="text-white hover:bg-zinc-800 focus:bg-zinc-800">Van (5-7)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Child equipment</Label>
              <div className="flex gap-4 mt-1.5">
                <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                  <input data-testid="acr-baby-seat" type="checkbox" checked={form.baby_seat} onChange={toggle("baby_seat")} className="accent-amber-500" />
                  Baby seat
                </label>
                <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                  <input data-testid="acr-booster-seat" type="checkbox" checked={form.booster_seat} onChange={toggle("booster_seat")} className="accent-amber-500" />
                  Booster seat
                </label>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Flight #</Label>
              <Input data-testid="acr-flight" value={form.flight_number} onChange={upd("flight_number")} placeholder="AF1234" className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Train #</Label>
              <Input data-testid="acr-train" value={form.train_number} onChange={upd("train_number")} placeholder="TGV 8421" className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Notes</Label>
              <Textarea data-testid="acr-notes" value={form.notes} onChange={upd("notes")} rows={2} className="bg-[#1A1A1A] border-zinc-700 text-white" placeholder="Special instructions..." />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs text-zinc-400 uppercase tracking-wider">Set price now (optional, EUR)</Label>
              <Input data-testid="acr-price" type="number" min={0} step="0.01" value={form.admin_price} onChange={upd("admin_price")} placeholder="Leave blank for pending price" className="bg-[#1A1A1A] border-zinc-700 text-white h-10" />
              <p className="text-[10px] text-zinc-600">Enter a price to jump straight to "Priced" — agency receives quote email.</p>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button type="button" onClick={onClose} variant="outline" className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</Button>
            <Button data-testid="acr-submit" type="submit" disabled={loading} className="flex-1 bg-amber-500 text-black font-semibold hover:bg-amber-600">
              {loading ? "..." : "Create Request"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
